import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  Search,
  AlertTriangle,
  DollarSign,
  Calendar,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import SaleCard from "@/components/sales/SaleCard";
import { ExportMenu } from "@/components/shared/ExportMenu";
import { useSalesAndPayments } from "@/context/SalesAndPaymentsContext";

export default function SalesTab() {
  const ctx = useSalesAndPayments();
  const {
    canEdit,
    activeSales,
    filteredSales,
    overdueSales,
    filteredReservedApartments,
    salesSearchQuery,
    setSalesSearchQuery,
    salesStatusFilter,
    setSalesStatusFilter,
    salesDateRange,
    setSalesDateRange,
    isSalesFilterOpen,
    setIsSalesFilterOpen,
    setSalesQuickDate,
    getSalesFilterLabel,
    salesExportData,
    salesExportColumns,
    payments,
    loadingSales,
    setEditingSale,
    setShowSaleForm,
    handleDeleteSale,
    markDefaultedMutation,
    getClient,
    getApartment,
    calculateRemainingBalance,
    checkOverduePayments,
  } = ctx;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex gap-3 text-sm">
          <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
            {activeSales.filter((s) => s.status === "active").length} Active
          </span>
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
            {activeSales.filter((s) => s.status === "completed").length} Completed
          </span>
          {overdueSales.length > 0 && (
            <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full font-medium flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {overdueSales.length} Overdue
            </span>
          )}
        </div>

        {canEdit && (
          <div className="flex gap-3 flex-wrap items-center">
            <Popover open={isSalesFilterOpen} onOpenChange={setIsSalesFilterOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Calendar className="w-4 h-4" />
                  {getSalesFilterLabel()}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80" align="end">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold text-slate-500 uppercase">
                      Quick Filters
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant={salesDateRange.type === "all" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSalesQuickDate("all")}
                        className="text-sm"
                      >
                        All Time
                      </Button>
                      <Button
                        variant={
                          salesDateRange.type === "thisWeek" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSalesQuickDate("thisWeek")}
                        className="text-sm"
                      >
                        This Week
                      </Button>
                      <Button
                        variant={
                          salesDateRange.type === "lastWeek" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSalesQuickDate("lastWeek")}
                        className="text-sm"
                      >
                        Last Week
                      </Button>
                      <Button
                        variant={
                          salesDateRange.type === "thisMonth" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSalesQuickDate("thisMonth")}
                        className="text-sm"
                      >
                        This Month
                      </Button>
                      <Button
                        variant={
                          salesDateRange.type === "lastMonth" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSalesQuickDate("lastMonth")}
                        className="text-sm"
                      >
                        Last Month
                      </Button>
                      <Button
                        variant={
                          salesDateRange.type === "custom" ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() =>
                          setSalesDateRange({ ...salesDateRange, type: "custom" })
                        }
                        className="text-sm"
                      >
                        Custom
                      </Button>
                    </div>
                  </div>
                  {salesDateRange.type === "custom" && (
                    <div className="space-y-3 pt-3 border-t">
                      <div className="space-y-2">
                        <Label htmlFor="salesStartDate">Start Date</Label>
                        <Input
                          id="salesStartDate"
                          type="date"
                          value={salesDateRange.startDate}
                          onChange={(e) =>
                            setSalesDateRange({
                              ...salesDateRange,
                              startDate: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="salesEndDate">End Date</Label>
                        <Input
                          id="salesEndDate"
                          type="date"
                          value={salesDateRange.endDate}
                          onChange={(e) =>
                            setSalesDateRange({
                              ...salesDateRange,
                              endDate: e.target.value,
                            })
                          }
                        />
                      </div>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <ExportMenu
              data={salesExportData}
              columns={salesExportColumns}
              title="Sales List"
              filename={`sales_${new Date().toISOString().split("T")[0]}`}
            />
            <Button
              onClick={() => {
                setEditingSale(null);
                setShowSaleForm(true);
              }}
              className="gap-2 bg-gradient-to-r from-slate-900 to-slate-700 hover:from-slate-800 hover:to-slate-600"
            >
              <Plus className="w-4 h-4" />
              New Sale
            </Button>
          </div>
        )}
      </div>

      {/* Sales Search */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Search by client name or unit number..."
            className="pl-10"
            value={salesSearchQuery}
            onChange={(e) => setSalesSearchQuery(e.target.value)}
          />
        </div>
        <Select value={salesStatusFilter} onValueChange={setSalesStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="reserved">Reserved</SelectItem>
          </SelectContent>
        </Select>
      </div>
        
      {/* Sales List */}
      {salesStatusFilter !== "reserved" && (
        <div className="space-y-4">
          <div className="grid gap-4 grid-cols-1">
            {loadingSales ? (
              <div className="col-span-full text-center py-10 text-slate-500">
                Loading sales...
              </div>
            ) : filteredSales.length === 0 ? (
              <div className="col-span-full text-center py-10 text-slate-500">
                No sales found matching your criteria.
              </div>
            ) : (
              filteredSales.map((sale) => (
                <SaleCard
                  key={sale.id}
                  sale={sale}
                  client={getClient(sale.client_id)}
                  apartment={getApartment(sale.apartment_id)}
                  remainingBalance={calculateRemainingBalance(sale)}
                  overdueInfo={checkOverduePayments(sale)}
                  payments={payments.filter((p) => p.sale_id === sale.id)}
                  onEdit={(s) => {
                    setEditingSale(s);
                    setShowSaleForm(true);
                  }}
                  onDelete={handleDeleteSale}
                  onMarkDefaulted={(s) => {
                    if (
                      window.confirm(
                        "Are you sure you want to mark this sale as defaulted? This will reset the apartment to available and archive the sale."
                      )
                    ) {
                      markDefaultedMutation.mutate(s);
                    }
                  }}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Overdue Sales Section */}
      {salesStatusFilter === "all" && overdueSales.length > 0 && (
        <div className="mt-8 space-y-4">
          <h3 className="text-lg font-semibold text-red-600 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Overdue Payments
          </h3>
          <div className="grid gap-4 grid-cols-1">
            {overdueSales.map((sale) => (
              <SaleCard
                key={`overdue-${sale.id}`}
                sale={sale}
                client={getClient(sale.client_id)}
                apartment={getApartment(sale.apartment_id)}
                remainingBalance={calculateRemainingBalance(sale)}
                overdueInfo={checkOverduePayments(sale)}
                payments={payments.filter((p) => p.sale_id === sale.id)}
                onEdit={
                  canEdit
                    ? () => {
                        setEditingSale(sale);
                        setShowSaleForm(true);
                      }
                    : undefined
                }
                onDelete={canEdit ? () => handleDeleteSale(sale) : undefined}
                onMarkDefaulted={
                  canEdit ? () => markDefaultedMutation.mutate(sale) : undefined
                }
                isOverdue
              />
            ))}
          </div>
        </div>
      )}

      {/* Reserved Section */}
      {salesStatusFilter === "reserved" && (
        <div className="mt-6 space-y-6">
          {filteredReservedApartments.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              No active reservations found.
            </div>
          ) : (
            <div className="grid gap-4 grid-cols-1">
              {filteredReservedApartments.map((reservation) => {
                const apartment = getApartment(reservation.apartment_id);
                return (
                  <div
                    key={reservation.id}
                    className="bg-white p-5 rounded-lg shadow-sm border border-slate-200"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="px-2 py-1 bg-amber-100 text-amber-800 border border-amber-200 rounded text-xs font-medium">
                        Reserved
                      </span>
                      <span className="text-xs text-slate-400">
                        {new Date(reservation.created_date).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-1">
                      {reservation.non_client_name}
                    </h3>
                    <p className="text-slate-600">
                      Unit {apartment?.unit_number || "N/A"} -{" "}
                      {apartment?.block || "N/A"}
                    </p>
                    <p className="text-sm text-slate-500 mt-2">
                      Reserved on:{" "}
                      {new Date(reservation.reservation_date).toLocaleDateString()}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}